import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { deleteCropVariety } from '../../../features/cropVarieties/cropVarietiesSlice';
import { useCropVarieties } from '../../../features/cropVarieties/hooks/useCropVarieties';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ErrorMessage from '../../ui/ErrorMessage';
import EditVarietyForm from './EditVarietyForm';
import AddVarietyForm from './AddVarietyForm';

interface Props {
  onClose: () => void;
}

const VarietiesList: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { varieties, loading, error } = useCropVarieties();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteError, setDeleteError] = useState<{ message: string; crops?: any[] } | null>(null);

  const handleDelete = async (id: string) => {
    if (window.confirm(t('common.areYouSure'))) {
      try {
        const result = await dispatch(deleteCropVariety(id));
        if (deleteCropVariety.rejected.match(result)) {
          try {
            const errorData = JSON.parse(result.payload as string);
            setDeleteError(errorData);
          } catch {
            setDeleteError({ message: result.payload as string });
          }
        }
      } catch (err) {
        setDeleteError({ message: 'Failed to delete variety' });
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{t('crops.varieties.title')}</h3>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowAddForm(!showAddForm)}
              variant="primary"
              size="sm"
            >
              {showAddForm ? t('common.cancel') : t('crops.addVariety')}
            </Button>
            <Button 
              variant="secondary" 
              onClick={onClose}
              size="sm"
            >
              {t('common.close')}
            </Button>
          </div>
        </div>

        {showAddForm && (
          <AddVarietyForm onSuccess={() => setShowAddForm(false)} />
        )}

        {deleteError && (
          <div className="mb-4">
            <ErrorMessage message={deleteError.message} />
            {deleteError.crops && (
              <div className="mt-2 text-sm">
                <p className="font-medium">Associated Crops:</p>
                <ul className="mt-1 space-y-1">
                  {deleteError.crops.map((crop) => (
                    <li key={crop.id} className="text-gray-600">
                      {crop.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {varieties.length === 0 ? (
            <p className="text-gray-500 text-center py-4 col-span-full">
              {t('crops.varieties.noVarieties')}
            </p>
          ) : (
            varieties.map(variety => (
              <div key={variety.id} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                {editingId === variety.id ? (
                  <EditVarietyForm
                    variety={variety}
                    onCancel={() => setEditingId(null)}
                    onSuccess={() => setEditingId(null)}
                  />
                ) : (
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{variety.name}</h4>
                      {variety.description && (
                        <p className="text-sm text-gray-600 mt-2">
                          {variety.description}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setEditingId(variety.id)}
                        className="flex-1"
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(variety.id)}
                        className="flex-1"
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
};

export default VarietiesList;